import React from 'react';
import { theme, Typography } from 'antd';

const { Text, Paragraph } = Typography;

/**
 * Shared layout primitives for the carbon indices screen.
 *
 * Colours come from the antd theme, never from constants declared here. A screen that
 * ships its own palette lands a half-shade off the rest of the admin and stops following
 * ConfigProvider (App.tsx sets colorPrimary #1f7a1f and borderRadius 2), so it drifts the
 * moment anyone retunes the theme.
 */

/** The only spacing source on this screen. */
export const S = { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 } as const;

/**
 * Comfortable reading measure. Prose and single-line inputs are capped at this; tables,
 * charts and the preview band stay full width because they are data, not text.
 */
export const MEASURE = 720;

/** Help text under a control. Capped so a line never runs the full page width. */
export const Help: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => {
  const { token } = theme.useToken();
  return (
    <Paragraph
      type="secondary"
      style={{
        maxWidth: wide ? 980 : MEASURE,
        marginBottom: S.md,
        color: token.colorTextDescription,
      }}
    >
      {children}
    </Paragraph>
  );
};

/**
 * A titled group inside a section, for one unit of work.
 *
 * Replaces `Divider orientation="left"`, which drew a stub of rule to the left of its
 * text and read as a rendering fault rather than a heading.
 */
export const Panel: React.FC<{
  title: React.ReactNode;
  aside?: React.ReactNode;
  children: React.ReactNode;
  tint?: boolean;
}> = ({ title, aside, children, tint }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: S.md,
        marginBottom: S.md,
        background: tint ? token.colorFillAlter : token.colorBgContainer,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: S.sm,
          marginBottom: S.sm,
        }}
      >
        <Text strong>{title}</Text>
        {aside}
      </div>
      {children}
    </div>
  );
};

